/**
 * Canonical 19-state warmup/account lifecycle (plan §3.1). Replaces the three
 * orthogonal representations (`EmailAccount.status`, DNS booleans, and
 * `EmailAccountWarmupState.warmupStatus`). Values === member name so the enum
 * round-trips cleanly through a MySQL ENUM column and queue payloads.
 *
 * Terminology rule: the six `SUSPENDED_*` states are "Suspended"
 * (system-initiated); "Paused" is reserved for `WARMUP_PAUSED` (user-initiated).
 *
 * NOTE: this file holds ONLY type/enum declarations (the package charter is
 * "type declarations only, no runtime dependencies"). The lookup tables and
 * helper functions that operate on these enums live in
 * `account-state.constant.ts` and are re-exported alongside this file.
 */
export enum AccountState {
  // Connection (5)
  DISCONNECTED = 'DISCONNECTED',
  CONNECTED = 'CONNECTED',
  DNS_CHECKING = 'DNS_CHECKING',
  DNS_FAILED = 'DNS_FAILED',
  DNS_PASSED = 'DNS_PASSED',
  // Mailbox send/receive verification (2) — the SECOND hard gate, after DNS.
  // A run exchanges one probe each way with a house-owned seed mailbox:
  // OUTBOUND (candidate -> seed) proves the mailbox can SEND, INBOUND
  // (seed -> candidate) proves it can RECEIVE. Both must land.
  //
  // These are PRE-WARMUP states only. Re-verifying an account that is already
  // WARMUP_ACTIVE leaves it WARMUP_ACTIVE for the whole run — moving a healthy
  // mailbox in here nightly would stop its sending 365 times a year to prove
  // it works. A confirmed re-verification failure goes straight to
  // SUSPENDED_VERIFICATION instead.
  MAILBOX_VERIFYING = 'MAILBOX_VERIFYING',
  MAILBOX_VERIFY_FAILED = 'MAILBOX_VERIFY_FAILED',
  // Warmup (5)
  WARMUP_READY = 'WARMUP_READY',
  WARMUP_ACTIVE = 'WARMUP_ACTIVE',
  WARMUP_THROTTLED = 'WARMUP_THROTTLED',
  MAINTENANCE = 'MAINTENANCE',
  WARMUP_PAUSED = 'WARMUP_PAUSED',
  // Suspended (7) — system-initiated, distinct from user pause
  SUSPENDED_AUTH = 'SUSPENDED_AUTH',
  SUSPENDED_BOUNCE = 'SUSPENDED_BOUNCE',
  SUSPENDED_SPAM = 'SUSPENDED_SPAM',
  SUSPENDED_BLACKLIST = 'SUSPENDED_BLACKLIST',
  SUSPENDED_PROVIDER = 'SUSPENDED_PROVIDER',
  SUSPENDED_PLATFORM = 'SUSPENDED_PLATFORM',
  // A mailbox that was warming and then failed re-verification: it stopped
  // sending or stopped receiving. Auto-resumes once a later run PASSES.
  SUSPENDED_VERIFICATION = 'SUSPENDED_VERIFICATION',
}

/**
 * The cause of a state transition. Recorded on every `WarmupStateTransition`
 * audit row alongside `Attribution`.
 */
export enum TransitionTrigger {
  USER_ENABLE = 'USER_ENABLE',
  USER_PAUSE = 'USER_PAUSE',
  USER_RESUME = 'USER_RESUME',
  USER_DISCONNECT = 'USER_DISCONNECT',
  RAMP_PAUSE = 'RAMP_PAUSE',
  THROTTLE = 'THROTTLE',
  THROTTLE_RECOVER = 'THROTTLE_RECOVER',
  GRADUATE = 'GRADUATE',
  MAINTENANCE_DECAY = 'MAINTENANCE_DECAY',
  SUSPEND_SPAM = 'SUSPEND_SPAM',
  SUSPEND_BOUNCE = 'SUSPEND_BOUNCE',
  SUSPEND_BLACKLIST = 'SUSPEND_BLACKLIST',
  SUSPEND_AUTH = 'SUSPEND_AUTH',
  SUSPEND_PROVIDER = 'SUSPEND_PROVIDER',
  SUSPEND_PLATFORM = 'SUSPEND_PLATFORM',
  AUTO_RESUME = 'AUTO_RESUME',
  DNS_CHECK_START = 'DNS_CHECK_START',
  DNS_CHECK_PASS = 'DNS_CHECK_PASS',
  DNS_CHECK_FAIL = 'DNS_CHECK_FAIL',
  CONNECT = 'CONNECT',
  WORKSPACE_DELETED = 'WORKSPACE_DELETED',
  // Mailbox send/receive verification. VERIFY_START is stamped when a
  // pre-warmup account enters MAILBOX_VERIFYING; VERIFY_PASS / VERIFY_FAIL on
  // the verdict. SUSPEND_VERIFICATION is the suspend-side trigger, kept
  // separate from VERIFY_FAIL because the two land in different states from
  // different prior states (pre-warmup vs already-warming).
  VERIFY_START = 'VERIFY_START',
  VERIFY_PASS = 'VERIFY_PASS',
  VERIFY_FAIL = 'VERIFY_FAIL',
  SUSPEND_VERIFICATION = 'SUSPEND_VERIFICATION',
  // Pool-tier moves from the daily pool evaluation. Recorded for the audit
  // trail only: the row's fromState === toState (a tier move never changes the
  // lifecycle state) and the tiers ride in `structuredStatus.pool_change`.
  // Excluded from the account's latest status and from the mailbox history.
  POOL_PROMOTE = 'POOL_PROMOTE',
  POOL_DEMOTE = 'POOL_DEMOTE',
}

/**
 * Who/what is accountable for a transition (plan §3.4 structured status).
 */
export enum Attribution {
  SENDER = 'sender',
  RECIPIENT = 'recipient',
  POOL = 'pool',
  PROVIDER = 'provider',
  PLATFORM = 'platform',
  USER = 'user',
  SYSTEM = 'system',
}

/**
 * Coarse bucket an `AccountState` collapses to. The 6 public buckets are the
 * `?status=` filter vocabulary (plan §3.2 + the `disconnected` bucket added
 * when `DISCONNECTED` was split out of the internal `setup` bucket so it's
 * independently filterable); `setup` remains the internal home for the other
 * five connection states (CONNECTED, DNS_CHECKING, DNS_FAILED, DNS_PASSED,
 * WARMUP_READY) and is NOT exposed as a filter option.
 *
 * `graduated` was removed alongside `WARMUP_GRADUATED`, the only state that
 * ever mapped to it — so the bucket had always been empty in production and
 * every graduated mailbox reported as `maintenance`. Graduation itself is
 * unaffected: it is recorded by `graduatedAt` plus a `WARMUP_GRADUATED`
 * NOTIFICATION (a separate vocabulary in `notification.interface.ts`), and the
 * account lands in `MAINTENANCE`. If a "past warmup" filter is wanted back, the
 * honest predicate is `MAINTENANCE AND graduatedAt IS NOT NULL` — a derived
 * bucket, not a state.
 */
export type StatusBucket =
  | 'active'
  | 'throttled'
  | 'paused'
  | 'suspended'
  // A rate-driven suspension the 30-min auto-resume sweep releases on its own
  // once `cooldownUntil` passes — see `isCooldownSuspension`. A REASON-derived
  // bucket: `SUSPENDED_BOUNCE` lands here or in `suspended` depending on why.
  | 'cooldown'
  | 'maintenance'
  | 'disconnected'
  | 'setup';
